/* eslint-disable typescript-enum/no-enum */
import { ContextManager, createActions, OnSignal } from 'dreamstate';

import { Callable, Optional } from '@vimeo/core/types/generic';
import { Logger } from '@vimeo/logging';

import {
  EAgoraSignal,
  ELiveGraphicsSignal,
  ELiveSignal,
  TAdminConnectionDetectedSignalEvent,
  TConfirmationNotificationClearSignalEvent,
  TConfirmationNotificationSignalEvent,
  TSimpleNotificationSignalEvent,
} from '../../store/signals';
import {
  T_CANT_CONNECT_TO_DEVICES,
  T_DEVICES_IN_USE,
  T_GOT_IT,
  T_OK,
  T_SWITCH_CAMERA,
  T_YOU_ARE_OFF_CAMERA_AND_MUTED,
  T_YOU_CAN_MANAGE_EVENT,
} from '../../translations';
import { CallablePlaceholder } from '../../types/general';
import {
  EConfirmationType,
  EConfirmationVariant,
  IConfirmationNotification,
  ISimpleNotification,
} from '../../types/notifications';

export enum EGlobalModalId {
  VIDEO_PICKER_MODAL = 'VIDEO_PICKER_MODAL',
}

export enum ENoticesList {
  VIDEO_PLAY_CONTROLS = 'VIDEO_PLAY_CONTROLS',
}

export interface INotificationContext {
  notificationActions: {
    openModalWithId: (id: EGlobalModalId) => void;
    closeModalWithId: (id: EGlobalModalId) => void;
    clearConfirmationNotification: Callable;
    declineConfirmationNotification: Callable;
    confirmConfirmationNotification: Callable;
    showSimpleNotification: (notification: ISimpleNotification) => void;
    clearSimpleNotification: Callable;
    setNoticeVisibility: (notice: ENoticesList, status: boolean) => void;
  };
  confirmationNotification: Optional<IConfirmationNotification>;
  simpleNotification: Optional<ISimpleNotification>;
  notices: Record<ENoticesList, Optional<boolean>>;
  openedModalsIds: Array<EGlobalModalId>;
}

/**
 * Global notifications manager for live applications.
 * todo: Once we can have few concurrent notifications, probably should add queue for them.
 */
export class NotificationManager extends ContextManager<INotificationContext> {
  public override context: INotificationContext = {
    notificationActions: createActions({
      clearConfirmationNotification: () => this.clearConfirmationNotification(),
      openModalWithId: (id: EGlobalModalId) => this.openModalWithId(id),
      closeModalWithId: (id: EGlobalModalId) => this.closeModalWithId(id),
      declineConfirmationNotification: () => this.declineConfirmationNotification(),
      confirmConfirmationNotification: () => this.confirmConfirmationNotification(),
      showSimpleNotification: (notification: ISimpleNotification) =>
        this.showSimpleNotification(notification),
      clearSimpleNotification: () => this.clearSimpleNotification(),
      setNoticeVisibility: (notice: ENoticesList, status: boolean) =>
        this.setNoticeVisibility(notice, status),
    }),
    confirmationNotification: null,
    simpleNotification: null,
    openedModalsIds: [],
    notices: {
      [ENoticesList.VIDEO_PLAY_CONTROLS]: null,
    },
  };

  private log: Logger = new Logger('🦍NM');

  /**
   * Open a modal with given id.
   */
  public openModalWithId(id: EGlobalModalId): void {
    this.log.info('Opening global modal:', id);
    this.setContext({ openedModalsIds: [...this.context.openedModalsIds, id] });
  }

  /**
   * Close a modal with given id.
   */
  public closeModalWithId(id: EGlobalModalId): void {
    this.log.info('Closing global modal:', id);
    this.setContext({ openedModalsIds: this.context.openedModalsIds.filter((it) => it !== id) });
  }

  /**
   * Clear confirmation notification displayed currently.
   */
  public clearConfirmationNotification(): void {
    this.setContext({ confirmationNotification: null });
  }

  /**
   * Clear notification displayed currently.
   */
  public clearSimpleNotification(): void {
    this.setContext({ simpleNotification: null });
  }

  /**
   * Decline current notification.
   */
  public declineConfirmationNotification(): void {
    const { confirmationNotification } = this.context;

    if (confirmationNotification) {
      setTimeout(confirmationNotification.onDecline || CallablePlaceholder);
      this.setContext({ confirmationNotification: null });
    }
  }

  /**
   * Confirm current notification.
   */
  public confirmConfirmationNotification(): void {
    const { confirmationNotification } = this.context;

    /**
     * Call handler in async way to prevent issues in this method.
     * todo: Probably should just call with inlineSafe.
     */
    if (confirmationNotification) {
      setTimeout(confirmationNotification.onConfirm);
      this.setContext({ confirmationNotification: null });
    }
  }

  /**
   * Show simple notification.
   */
  public showSimpleNotification(simpleNotification: ISimpleNotification): void {
    this.setContext({ simpleNotification });
  }

  public setNoticeVisibility(noticeId: ENoticesList, status: boolean): void {
    this.setContext({ notices: { ...this.context.notices, [noticeId]: status } });
  }

  /**
   * Show confirmation notification.
   */
  public showConfirmationNotification(notification: IConfirmationNotification): void {
    this.setContext({
      confirmationNotification: {
        ...notification,
        variant: notification.variant || EConfirmationVariant.GLOBAL,
      },
    });
  }

  /**
   * Received some confirmation notification from application.
   */
  @OnSignal(ELiveSignal.CONFIRMATION_NOTIFICATION)
  public onConfirmationNeeded(signalEvent: TConfirmationNotificationSignalEvent): void {
    const { data: confirmationNotification } = signalEvent;

    if (confirmationNotification) {
      this.log.info('Confirmation notification received:', confirmationNotification);
      this.showConfirmationNotification(confirmationNotification);
    } else {
      this.log.error('Tried to show invalid notification with a signal');
    }
  }

  /**
   * Received some simple notification from application.
   */
  @OnSignal(ELiveSignal.SHOW_SIMPLE_NOTIFICATION)
  public onShowNotification(signalEvent: TSimpleNotificationSignalEvent): void {
    const { data: notification } = signalEvent;

    if (notification) {
      this.showSimpleNotification(notification);
    } else {
      this.log.error('Tried to show invalid notification with a signal');
    }
  }

  /**
   * Received clean up event from within scope.
   */
  @OnSignal(ELiveSignal.CONFIRMATION_NOTIFICATION_CLEAR)
  public onConfirmationClear(signalEvent: TConfirmationNotificationClearSignalEvent): void {
    const { data: type } = signalEvent;
    const { confirmationNotification } = this.context;

    if (!confirmationNotification) {
      return;
    }

    if (!type || confirmationNotification.type === type) {
      this.setContext({ confirmationNotification: null });
    }
  }

  /**
   * Received local media device is occupied by another application error
   */
  @OnSignal(EAgoraSignal.LOCAL_MEDIA_DEVICE_OCCUPIED_BY_ANOTHER_APP)
  public onLocalMediaDeviceOccupied(): void {
    const confirmationNotification: IConfirmationNotification = {
      type: EConfirmationType.MEDIA_DEVICE_OCCUPIED_BY_ANOTHER_APPLICATION,
      body: T_CANT_CONNECT_TO_DEVICES,
      header: T_DEVICES_IN_USE,
      declineMessage: null,
      confirmMessage: T_OK,
      modalWidth: 390,
    };
    this.showConfirmationNotification(confirmationNotification);
  }

  /**
   * Received initial admin connection detected event
   */
  @OnSignal(ELiveSignal.ADMIN_CONNECTION_TYPE_DETECTED)
  public onAdminConnectionTypeDetected(signal: TAdminConnectionDetectedSignalEvent): void {
    const {
      data: { switchToBroadcasterMode },
    } = signal;
    const confirmationNotification: IConfirmationNotification = {
      type: EConfirmationType.ADMIN_CONNECTION_DETECTED,
      body: T_YOU_CAN_MANAGE_EVENT,
      onDecline: switchToBroadcasterMode,
      header: T_YOU_ARE_OFF_CAMERA_AND_MUTED,
      confirmMessage: T_GOT_IT,
      declineMessage: T_SWITCH_CAMERA,
      modalWidth: 390,
    };
    this.showConfirmationNotification(confirmationNotification);
  }

  /**
   * Received video controls disabled notice event
   */
  @OnSignal(ELiveGraphicsSignal.SHOW_VIDEO_PLAY_CONTROLS_DISABLED_NOTICE)
  public onShowVideoActionButtonsDisabledNotice(): void {
    if (this.context.notices[ENoticesList.VIDEO_PLAY_CONTROLS] === null) {
      this.setNoticeVisibility(ENoticesList.VIDEO_PLAY_CONTROLS, true);
    }
  }
}
